import axios from "axios";
import { getToken } from "./userService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

/**
 * Request a payout
 * @param {Object} payoutData - Payout request data
 * @param {number} payoutData.amount - Amount to withdraw
 * @param {string} payoutData.currency - Currency code (default: USD)
 * @returns {Promise<Object>} Payout request response
 */
export async function requestPayout(payoutData) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/payouts/request`,
      {
        amount: payoutData.amount,
        currency: payoutData.currency || 'USD',
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error requesting payout:", error);
    throw error;
  }
}

/**
 * Get payout history
 * @returns {Promise<Array>} List of payout requests
 */
export async function getPayoutHistory() {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/payouts/history`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data?.payouts || [];
  } catch (error) {
    // console.error("Error fetching payout history:", error);
    return [];
  }
}

/**
 * Get payout status
 * @param {string} payoutId - Payout request ID
 * @returns {Promise<Object>} Payout status
 */
export async function getPayoutStatus(payoutId) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/payouts/${payoutId}/status`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error fetching payout status:", error);
    throw error;
  }
}

// ============================================
// ADMIN ENDPOINTS
// ============================================

/**
 * Get pending payout requests (Admin only)
 * @returns {Promise<Array>} List of pending payout requests
 */
export async function getPendingPayouts() {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/payouts/pending`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching pending payouts:", error);
    throw error;
  }
}

/**
 * Get all payouts with optional status filter (Admin only)
 * @param {string} status - Optional status filter (pending_approval, approved, processing, completed, rejected, failed)
 * @returns {Promise<Array>} List of payout requests
 */
export async function getAllPayouts(status = null) {
  try {
    const params = status ? { status } : {};
    const response = await axios.get(
      `${API_BASE_URL}/admin/payouts/all`,
      {
        params,
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching all payouts:", error);
    throw error;
  }
}

/**
 * Approve a payout request (Admin only)
 * @param {string} payoutId - Payout request ID
 * @param {string} notes - Optional admin notes
 * @returns {Promise<Object>} Approval result
 */
export async function approvePayout(payoutId, notes = '') {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/payouts/${payoutId}/approve`,
      { notes },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error approving payout:", error);
    throw error;
  }
}

/**
 * Reject a payout request (Admin only)
 * @param {string} payoutId - Payout request ID
 * @param {string} reason - Required rejection reason
 * @returns {Promise<Object>} Rejection result
 */
export async function rejectPayout(payoutId, reason) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/payouts/${payoutId}/reject`,
      { reason },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error rejecting payout:", error);
    throw error;
  }
}

